// ⚙️ 상태 변경 섹션 컴포넌트
import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  FormControl,
  Select,
  MenuItem,
  Button,
  Checkbox,
  FormControlLabel,
  Collapse,
  Alert,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  NotificationsActive as NotificationIcon,
} from '@mui/icons-material';
import { APPLICATION_STATUS_LIST } from '../../../../../constants/statusList';
import { APPLICATION_STATUS_COLORS, getDeviceStatusColor } from '../../../../../constants/statusColors';
import type { ActualApplicationStatus } from '../types/applicationStickyHeader.types';
import type { Application } from '../../../types/application';

interface StatusManagementSectionProps {
  application?: Application; // 🆕 신청서 정보 (거래처 여부 확인용)
  tempStatus: ActualApplicationStatus;
  isStatusChanged: boolean;
  isStatusSaving: boolean;
  sendAlert: boolean;
  alertPreviewMessage: string;
  canSendAlert: boolean;
  // 가격안내 선택 발송 관련 추가 props
  selectedDeviceIds?: string[];
  perDevicePreviews?: Array<{
    deviceId: string;
    deviceModel: string;
    grade?: string | null;
    priceStr: string;
    event: string;
    message: string;
    errors: string[];
    status: string;
  }>;
  onToggleDevice?: (deviceId: string) => void;
  onToggleAll?: (checked: boolean) => void;
  onStatusChange?: (status: string) => Promise<void>;
  onStatusChangeHandler: (newStatus: ActualApplicationStatus) => void;
  onStatusSave: () => Promise<void>;
  onStatusReset: () => void;
  onAlertCheckChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPreviewOpen: () => void;
  // 🆕 정보입력요청 관련 (기존 AlertPreviewDialog 재활용)
  onInfoRequestSelect?: () => void;
}

/**
 * 상태 변경 및 알림톡 섹션 컴포넌트
 * - 상태 선택 드롭다운
 * - 알림톡 전송 옵션
 * - 저장/리셋 버튼
 */
export const StatusManagementSection: React.FC<StatusManagementSectionProps> = ({
  application,
  tempStatus,
  isStatusChanged,
  isStatusSaving,
  sendAlert,
  alertPreviewMessage,
  canSendAlert,
  selectedDeviceIds,
  perDevicePreviews,
  onToggleDevice,
  onToggleAll,
  onStatusChange,
  onStatusChangeHandler,
  onStatusSave,
  onStatusReset,
  onAlertCheckChange,
  onPreviewOpen,
  onInfoRequestSelect,
}) => {
  // 🆕 거래처/수동 신청 여부 확인
  const isPartnerOrManual = application?.source_type === 'partner' || application?.source_type === 'manual';

  return (
    <Grid item xs={12} md={4}>
      <Box
        sx={{
          p: 2,
          bgcolor: 'rgba(103, 58, 183, 0.04)',
          borderRadius: 2,
          border: '1px solid rgba(103, 58, 183, 0.1)',
          height: '100%',
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              상태 변경 및 알림톡 발송
            </Typography>
            {isStatusChanged && (
              <Chip
                label="변경됨"
                size="small"
                sx={{
                  bgcolor: 'warning.main',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Stack>

          <FormControl fullWidth size="small">
            <Select
              value={tempStatus}
              onChange={(e) => {
                const value = e.target.value;
                // 🆕 정보입력요청 선택 시 기존 AlertPreviewDialog를 통해 미리보기 표시
                if (value === '정보입력요청') {
                  onInfoRequestSelect?.();
                  return;
                }
                onStatusChangeHandler(value as ActualApplicationStatus);
              }}
              disabled={!onStatusChange || isStatusSaving}
              sx={{
                borderRadius: 2,
                bgcolor: '#f8fafc',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isStatusChanged ? '#ff9800' : '#e1e8ed',
                  borderWidth: isStatusChanged ? 2 : 1,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: isStatusChanged ? '#f57c00' : '#1976d2',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1976d2',
                },
              }}
            >
              {APPLICATION_STATUS_LIST.filter(status => status !== '전체').map((status) => (
                <MenuItem key={status} value={status}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: APPLICATION_STATUS_COLORS[status] || '#757575',
                      }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {status}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
              
              {/* 🆕 정보입력요청 항목 (거래처/수동 신청 전용) */}
              {isPartnerOrManual && (
                <MenuItem 
                  value="정보입력요청"
                  disabled={isStatusSaving}
                  sx={{
                    borderTop: '1px solid #e1e8ed',
                    mt: 0.5,
                    pt: 1,
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: '#7c3aed',
                      }}
                    />
                    <Typography variant="body2" fontWeight={500} sx={{ color: '#7c3aed' }}>
                      정보입력 요청
                    </Typography>
                    <Chip
                      label="거래처 전용"
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '0.65rem',
                        fontWeight: 700,
                        bgcolor: '#7c3aed',
                        color: '#fff',
                        '& .MuiChip-label': { px: 0.8 },
                      }}
                    />
                  </Stack>
                </MenuItem>
              )}
            </Select>
          </FormControl>

          {/* 🔔 알림톡 전송 옵션 */}
          <Collapse in={canSendAlert} timeout={300}>
            <Box
              sx={{
                p: 2,
                bgcolor: 'rgba(25, 118, 210, 0.04)',
                borderRadius: 2,
                border: '1px solid rgba(25, 118, 210, 0.1)',
              }}
            >
              <Stack spacing={1.5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendAlert}
                      onChange={onAlertCheckChange}
                      sx={{
                        color: 'primary.main',
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                      }}
                    />
                  }
                  label={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <NotificationIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                      <Typography variant="body2" fontWeight={600}>
                        고객에게 알림톡 전송
                      </Typography>
                    </Stack>
                  }
                />

                {/* 가격안내일 때: per-device 선택 UI */}
                {tempStatus === '가격안내' && sendAlert && (
                  <Box sx={{ p: 1.5, border: '1px dashed #bbdefb', borderRadius: 2, bgcolor: '#f0f7ff' }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                      <Typography variant="caption" color="primary.main" fontWeight={700}>
                        가격안내 대상 기기 선택 (기기명 · 가격 · 상태)
                      </Typography>
                      <FormControlLabel
                        control={<Checkbox checked={!!selectedDeviceIds && selectedDeviceIds.length === (perDevicePreviews?.length || 0)} onChange={(e) => onToggleAll && onToggleAll(e.target.checked)} />}
                        label={<Typography variant="caption">전체 선택</Typography>}
                      />
                    </Stack>
                    <Stack spacing={0.5}>
                      {(perDevicePreviews || []).map((p) => {
                        const checked = !!selectedDeviceIds?.includes(p.deviceId);
                        return (
                          <FormControlLabel
                          key={p.deviceId}
                          control={<Checkbox checked={checked} onChange={() => onToggleDevice && onToggleDevice(p.deviceId)} />}
                          label={
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr auto', sm: '1fr auto auto' },
                                alignItems: 'center',
                                columnGap: 0.5,
                                width: '100%',
                                opacity: checked ? 1 : 0.6,
                              }}
                            >
                              <Tooltip title={p.deviceModel} placement="top" arrow>
                                <Chip 
                                  label={p.deviceModel}
                                  size="small"
                                  variant={checked ? 'filled' : 'outlined'}
                                  sx={{
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    '.MuiChip-label': {
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'block',
                                      maxWidth: '100%',
                                    },
                                  }}
                                />
                              </Tooltip>
                              <Chip 
                                label={`${p.priceStr}원`} 
                                size="small" 
                                color="warning"
                                sx={{ justifySelf: 'end', whiteSpace: 'nowrap' }}
                              />
                              <Chip 
                                label={p.status || '검수중'} 
                                size="small" 
                                sx={{ 
                                  justifySelf: 'end', 
                                  display: { xs: 'none', sm: 'inline-flex' },
                                  bgcolor: getDeviceStatusColor(p.status || '검수중'),
                                  color: 'white',
                                  '& .MuiChip-label': { fontWeight: 600 }
                                }}
                              />
                            </Box>
                          }
                        />
                        );
                      })}
                    </Stack>
                  </Box>
                )}

                {sendAlert && alertPreviewMessage && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<VisibilityIcon />}
                    onClick={onPreviewOpen}
                    sx={{
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': {
                        borderColor: 'primary.dark',
                        bgcolor: 'primary.50',
                      },
                    }}
                  >
                    메시지 미리보기
                  </Button>
                )}

                {!sendAlert && alertPreviewMessage && (
                  <Alert severity="info" sx={{ borderRadius: 2 }}>
                    <Typography variant="caption">
                      💡 체크하면 상태 변경 시 고객에게 알림톡을 발송합니다. 저장 전 미리보기를 통해 안전하게 확인 가능합니다.
                    </Typography>
                  </Alert>
                )}
              </Stack>
            </Box>
          </Collapse>

          {/* 💾 저장/리셋 버튼 */}
          {isStatusChanged && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                fullWidth
                startIcon={isStatusSaving ? <SaveIcon /> : (sendAlert && alertPreviewMessage ? <SendIcon /> : <SaveIcon />)}
                onClick={onStatusSave}
                disabled={isStatusSaving}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: sendAlert && alertPreviewMessage ? '#1976d2' : '#4caf50',
                  '&:hover': { 
                    bgcolor: sendAlert && alertPreviewMessage ? '#1565c0' : '#45a049' 
                  },
                  boxShadow: sendAlert && alertPreviewMessage 
                    ? '0 2px 8px rgba(25, 118, 210, 0.3)' 
                    : '0 2px 8px rgba(76, 175, 80, 0.3)',
                }}
              >
                {isStatusSaving 
                  ? '처리 중...' 
                  : (sendAlert && alertPreviewMessage ? '저장 & 알림톡 전송' : '저장')
                }
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={onStatusReset}
                disabled={isStatusSaving}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  minWidth: 60,
                  borderColor: '#e0e7ef',
                  color: '#64748b',
                  '&:hover': {
                    borderColor: '#cbd5e1',
                    bgcolor: '#f8fafc',
                  },
                }}
              >
                취소
              </Button>
            </Stack>
          )}

        </Stack>
      </Box>
    </Grid>
  );
};